"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { PipSnapConfig } from "./pip-config";
import { DEFAULT_PIP_SNAP_CONFIG } from "./pip-config";
import {
	isPipResizeCornerPinned,
	resolvePipBoundsAwareResizeLayout,
	resolvePipCornerPoint,
	resolvePipPositionFromFixedCorner,
	resolvePipResizeBoundsPadding,
	resolvePipResizeEdgePinState,
	resolvePipResizeFixedCorner,
	resolvePipSnapCycleResizeLayout,
	resolveSnappedPipCornerPoint,
	scalePipDimensions,
	snapPipScale,
} from "./pip-resize";
import type { PipResizeConfig, PipResizeCorner } from "./pip-resize-config";
import type {
	PipBounds,
	PipDimensions,
	PipPosition,
	PipSnapInsets,
} from "./pip-snap";
import { resolvePointerPositionInStage } from "./pip-stage-coordinates";

export interface PipResizeLayout {
	scale: number;
	position: PipPosition;
	dimensions: PipDimensions;
}

interface PipResizeSession {
	draggedCorner: PipResizeCorner;
	fixedCorner: PipResizeCorner;
	fixedPoint: PipPosition;
	startScale: number;
	startDimensions: PipDimensions;
}

export interface UseFloatingPipResizeOptions {
	enabled: boolean;
	boundsStageRef: React.RefObject<HTMLElement | null>;
	baseDimensions: PipDimensions;
	config: PipResizeConfig;
	snapConfig?: PipSnapConfig;
	snapInsets?: PipSnapInsets;
	positionRef: React.RefObject<PipPosition>;
	onLayoutChange: (layout: PipResizeLayout) => void;
}

export interface UseFloatingPipResizeResult {
	scale: number;
	dimensions: PipDimensions;
	isResizing: boolean;
	onResizePointerDown: (
		corner: PipResizeCorner,
	) => React.PointerEventHandler<HTMLElement>;
	onResizeDoubleClick: (
		corner: PipResizeCorner,
	) => React.MouseEventHandler<HTMLElement>;
	onResizePointerMove: React.PointerEventHandler<HTMLElement>;
	onResizePointerUp: React.PointerEventHandler<HTMLElement>;
}

function readBoundsFromStage(stage: HTMLElement | null): PipBounds | null {
	if (!stage) {
		return null;
	}
	const rect = stage.getBoundingClientRect();
	return { width: rect.width, height: rect.height };
}

export function useFloatingPipResize(
	options: UseFloatingPipResizeOptions,
): UseFloatingPipResizeResult {
	const {
		enabled,
		boundsStageRef,
		baseDimensions,
		config,
		snapConfig = DEFAULT_PIP_SNAP_CONFIG,
		snapInsets,
		positionRef,
		onLayoutChange,
	} = options;
	const [scale, setScale] = useState(1);
	const [isResizing, setIsResizing] = useState(false);
	const [resizePointerActive, setResizePointerActive] = useState(false);
	const isResizingRef = useRef(false);
	const resizePendingRef = useRef(false);
	const scaleRef = useRef(1);
	const activePointerIdRef = useRef<number | null>(null);
	const activeCornerRef = useRef<PipResizeCorner | null>(null);
	const captureTargetRef = useRef<HTMLElement | null>(null);
	const resizeSessionRef = useRef<PipResizeSession | null>(null);
	const pinStateRef = useRef(
		resolvePipResizeEdgePinState({
			position: { x: 0, y: 0 },
			dimensions: { widthPx: 1, heightPx: 1 },
			bounds: { width: 0, height: 0 },
			padding: { top: 0, right: 0, bottom: 0, left: 0 },
		}),
	);
	const baseDimensionsRef = useRef(baseDimensions);
	const configRef = useRef(config);
	const snapConfigRef = useRef(snapConfig);
	const snapInsetsRef = useRef(snapInsets);
	const onLayoutChangeRef = useRef(onLayoutChange);

	baseDimensionsRef.current = baseDimensions;
	configRef.current = config;
	snapConfigRef.current = snapConfig;
	snapInsetsRef.current = snapInsets;
	onLayoutChangeRef.current = onLayoutChange;

	useEffect(() => {
		isResizingRef.current = isResizing;
	}, [isResizing]);

	const emitLayout = useCallback((layout: PipResizeLayout, snap: boolean) => {
		const activeConfig = configRef.current;
		const resolvedScale = snap
			? snapPipScale(layout.scale, activeConfig)
			: layout.scale;
		const resolvedDimensions = scalePipDimensions(
			baseDimensionsRef.current,
			resolvedScale,
		);

		scaleRef.current = resolvedScale;
		setScale(resolvedScale);
		onLayoutChangeRef.current({
			scale: resolvedScale,
			position: layout.position,
			dimensions: resolvedDimensions,
		});
	}, []);

	const finishResize = useCallback(
		(event: Pick<PointerEvent, "pointerId">) => {
			const wasPending = resizePendingRef.current;
			resizePendingRef.current = false;

			if (!isResizingRef.current || activeCornerRef.current == null) {
				if (wasPending) {
					captureTargetRef.current?.releasePointerCapture(
						event.pointerId,
					);
					captureTargetRef.current = null;
					activeCornerRef.current = null;
					resizeSessionRef.current = null;
					activePointerIdRef.current = null;
					setResizePointerActive(false);
				}
				return;
			}

			const session = resizeSessionRef.current;
			activeCornerRef.current = null;
			resizeSessionRef.current = null;
			isResizingRef.current = false;
			captureTargetRef.current?.releasePointerCapture(event.pointerId);
			captureTargetRef.current = null;

			if (session != null) {
				const snappedScale = snapPipScale(
					scaleRef.current,
					configRef.current,
				);
				const dimensions = scalePipDimensions(
					baseDimensionsRef.current,
					snappedScale,
				);
				emitLayout(
					{
						scale: snappedScale,
						position: resolvePipPositionFromFixedCorner(
							session.fixedCorner,
							session.fixedPoint,
							dimensions,
						),
						dimensions,
					},
					false,
				);
			}

			setIsResizing(false);
			setResizePointerActive(false);
			activePointerIdRef.current = null;
		},
		[emitLayout, positionRef],
	);

	const onResizePointerDown = useCallback(
		(corner: PipResizeCorner): React.PointerEventHandler<HTMLElement> =>
			(event) => {
				if (!enabled) {
					return;
				}
				event.stopPropagation();
				event.preventDefault();

				const boundsStage = boundsStageRef.current;
				const bounds = readBoundsFromStage(boundsStage);
				if (!bounds) {
					return;
				}

				const position = positionRef.current;
				const startDimensions = scalePipDimensions(
					baseDimensionsRef.current,
					scaleRef.current,
				);
				const padding = resolvePipResizeBoundsPadding(
					snapConfigRef.current,
					snapInsetsRef.current,
				);
				const pinState = resolvePipResizeEdgePinState({
					position,
					dimensions: startDimensions,
					bounds,
					padding,
				});
				const fixedCorner = resolvePipResizeFixedCorner(
					corner,
					pinState,
				);
				const fixedCornerPoint = isPipResizeCornerPinned(
					fixedCorner,
					pinState,
				)
					? resolveSnappedPipCornerPoint(
							fixedCorner,
							position,
							startDimensions,
							bounds,
							padding,
							pinState,
						)
					: resolvePipCornerPoint(
							fixedCorner,
							position,
							startDimensions,
						);

				const target = event.currentTarget;
				target.setPointerCapture(event.pointerId);
				captureTargetRef.current = target;
				activePointerIdRef.current = event.pointerId;
				activeCornerRef.current = corner;
				isResizingRef.current = false;
				resizePendingRef.current = true;
				setResizePointerActive(true);
				pinStateRef.current = pinState;
				resizeSessionRef.current = {
					draggedCorner: corner,
					fixedCorner,
					fixedPoint: fixedCornerPoint,
					startScale: scaleRef.current,
					startDimensions,
				};
			},
		[boundsStageRef, enabled, positionRef],
	);

	const cancelResizeInteraction = useCallback(() => {
		const captureTarget = captureTargetRef.current;
		const pointerId = activePointerIdRef.current;
		if (captureTarget != null && pointerId != null) {
			captureTarget.releasePointerCapture(pointerId);
		}
		resizePendingRef.current = false;
		activeCornerRef.current = null;
		resizeSessionRef.current = null;
		isResizingRef.current = false;
		captureTargetRef.current = null;
		activePointerIdRef.current = null;
		setIsResizing(false);
		setResizePointerActive(false);
	}, []);

	const onResizeDoubleClick = useCallback(
		(corner: PipResizeCorner): React.MouseEventHandler<HTMLElement> =>
			(event) => {
				if (!enabled) {
					return;
				}
				event.stopPropagation();
				event.preventDefault();
				cancelResizeInteraction();

				const boundsStage = boundsStageRef.current;
				const bounds = readBoundsFromStage(boundsStage);
				if (!bounds) {
					return;
				}

				const layout = resolvePipSnapCycleResizeLayout({
					draggedCorner: corner,
					position: positionRef.current,
					currentScale: scaleRef.current,
					baseDimensions: baseDimensionsRef.current,
					bounds,
					padding: resolvePipResizeBoundsPadding(
						snapConfigRef.current,
						snapInsetsRef.current,
					),
					config: configRef.current,
				});
				emitLayout(layout, false);
			},
		[
			boundsStageRef,
			cancelResizeInteraction,
			emitLayout,
			enabled,
			positionRef,
		],
	);

	const onResizePointerMove = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (activeCornerRef.current == null) {
				return;
			}
			if (!isResizingRef.current && !resizePendingRef.current) {
				return;
			}
			const session = resizeSessionRef.current;
			const boundsStage = boundsStageRef.current;
			const bounds = readBoundsFromStage(boundsStage);
			if (!session || !boundsStage || !bounds) {
				return;
			}

			if (resizePendingRef.current && !isResizingRef.current) {
				resizePendingRef.current = false;
				isResizingRef.current = true;
				setIsResizing(true);
			}

			event.stopPropagation();
			const pointerPosition = resolvePointerPositionInStage(
				event,
				boundsStage,
			);
			const padding = resolvePipResizeBoundsPadding(
				snapConfigRef.current,
				snapInsetsRef.current,
			);
			const layout = resolvePipBoundsAwareResizeLayout({
				draggedCorner: session.draggedCorner,
				fixedCorner: session.fixedCorner,
				fixedPoint: session.fixedPoint,
				pointerPosition,
				startDimensions: session.startDimensions,
				startScale: session.startScale,
				pinState: pinStateRef.current,
				baseDimensions: baseDimensionsRef.current,
				bounds,
				padding,
				minScale: configRef.current.minScale,
				maxScale: configRef.current.maxScale,
			});
			emitLayout(layout, false);
		},
		[boundsStageRef, emitLayout],
	);

	const onResizePointerUp = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (!isResizingRef.current && !resizePendingRef.current) {
				return;
			}
			event.stopPropagation();
			finishResize(event);
		},
		[finishResize],
	);

	useEffect(() => {
		if (!isResizing && !resizePointerActive) {
			return;
		}

		const handleWindowPointerUp = (event: PointerEvent) => {
			finishResize(event);
		};

		window.addEventListener("pointerup", handleWindowPointerUp);
		window.addEventListener("pointercancel", handleWindowPointerUp);
		return () => {
			window.removeEventListener("pointerup", handleWindowPointerUp);
			window.removeEventListener("pointercancel", handleWindowPointerUp);
		};
	}, [finishResize, isResizing, resizePointerActive]);

	const dimensions = scalePipDimensions(baseDimensions, scale);

	return {
		scale,
		dimensions,
		isResizing,
		onResizePointerDown,
		onResizeDoubleClick,
		onResizePointerMove,
		onResizePointerUp,
	};
}
